import { flexRender, Table } from '@tanstack/react-table';
import { uniqueId } from 'lodash';
import React, { useMemo } from 'react';

import type { DataTableProps } from './types';

type DataTableBodyProps<T extends object> = Pick<
  DataTableProps<T>,
  'columns' | 'isLoading' | 'pageSize' | 'getRowClassName'
> & {
  table: Table<T>;
};

export const DataTableBody = <T extends object>({
  table,
  columns,
  isLoading,
  pageSize,
  getRowClassName,
}: DataTableBodyProps<T>): React.JSX.Element => {
  const skeletonRowIds = useMemo(
    () => Array.from({ length: pageSize ?? 0 }, () => uniqueId()),
    [pageSize],
  );

  const skeletonCellIds = useMemo(
    () => columns.map(() => uniqueId()),
    [columns.length],
  );

  if (isLoading) {
    return (
      <>
        {skeletonRowIds.map((rowId) => (
          <tr key={rowId} className="border-t border-gray-100">
            {skeletonCellIds.map((cellId) => (
              <td key={cellId} className="px-4 py-3">
                <div className="h-3 bg-gray-100 rounded-full animate-pulse" />
              </td>
            ))}
          </tr>
        ))}
      </>
    );
  }

  if (table.getRowModel().rows.length === 0) {
    return (
      <tr>
        <td
          colSpan={columns.length}
          className="px-4 py-8 text-sm text-center text-gray-500 border-t border-gray-100 bg-white"
        >
          No rows to display.
        </td>
      </tr>
    );
  }

  return (
    <>
      {table.getRowModel().rows.map((row) => {
        const extra = getRowClassName?.(row.original) ?? '';
        return (
          <tr
            key={row.id}
            className={`border-t border-gray-100 odd:bg-white even:bg-gray-50/60 hover:bg-blue-50/60 transition-colors ${extra}`}
          >
            {row.getVisibleCells().map((cell) => {
              const meta = cell.column.columnDef.meta as
                | { align?: 'left' | 'center' | 'right'; width?: string }
                | undefined;
              const align = meta?.align ?? 'left';
              const alignClass = align === 'center'
                ? 'text-center'
                : align === 'right'
                  ? 'text-right'
                  : 'text-left';
              return (
                <td
                  key={cell.id}
                  className={`px-4 py-3 text-sm text-gray-700 align-middle ${alignClass} ${meta?.width ?? ''}`}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
};
